<template>
  <div class="w-100">
    <div
      class="left-nav-item w-100"
      :class="{
        activated: tabActive,
        disabled: page.disabled,
      }"
      role="button"
      :aria-pressed="tabActive"
      :aria-disabled="page.disabled === true"
      :tabindex="page.disabled ? -1 : 0"
      @click="onClick(page)"
      @keydown.enter.prevent="onClick(page)"
      @keydown.space.prevent="onClick(page)"
    >
      <div class="item-text-container d-flex align-items-center">
        <BImg
          class="nav-icon"
          :src="tabActive ? page.imgActive : page.imgInactive"
          :alt="page.name || ''"
        />
        <transition name="fade">
          <span
            v-show="page.name"
            class="item-text"
          >{{ page.name }}</span>
        </transition>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">

// exported helper so tests can import and assert path matching logic
function isPathActive(currentPath: string, targetPath?: string) {
  if (!targetPath) return false;

  const normalize = (value: string) => value.replace(/\/+$/, "") || "/";
  const current = normalize(currentPath);
  const desired = normalize(targetPath);

  return current === desired || current.startsWith(`${desired}/`);
}

import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { BImg } from 'bootstrap-vue-next';

interface LeftNavPageConfig {
  name?: string;
  path?: string;
  imgActive?: string;
  imgInactive?: string;
  disabled?: boolean;
}

const props = defineProps<{
  page: LeftNavPageConfig;
  shouldEmitClick?: boolean;
}>();

const emit = defineEmits(['click']);

const route = useRoute();
const router = useRouter();

const tabActive = computed(() => isPathActive(route.path, props.page?.path));

function onClick(page: LeftNavPageConfig) {
  if (page?.disabled) {
    return;
  }

  if (props.shouldEmitClick) {
    emit('click');
    return;
  }

  const targetPath = page?.path;
  if (!targetPath || route.path === targetPath) {
    return;
  }

  void router.push(targetPath);
}
</script>
<style lang="scss" scoped>
.left-nav-item {
  background-color: transparent;
  cursor: pointer;
  vertical-align: middle;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  &.disabled {
    background-image: none !important;
    opacity: 0.3;
    cursor: default;
  }
  &.activated {
    color: blue;
  }
  &:hover:not(.activated):not(.disabled) {
    color: blue;
  }
}
.nav-icon {
  margin-right: 10px;
}
.fade-enter-active {
  transition: opacity 0.2s;
  transition-delay: 0.2s;
  transition-timing-function: ease-out;
}
.fade-leave-active {
  transition: opacity 0.2s;
  transition-timing-function: ease-out;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
